import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  CardText,
  Row,
  Col,
  Table,
  CardTitle,
  Badge,
} from "reactstrap";
import { TXElement } from "./TXElement";
import { gql } from "apollo-boost";
import { useQuery } from "@apollo/react-hooks";
import { IsErrorOrLoading } from "../../utils/IsErrorOrLoading";
import { Redirect, useHistory, useParams } from "react-router-dom";
import { AccountDetails } from "./AccountDetails";
import { Spinner } from "../../../UI/spinner/Spinner";
import { AccountAlert } from "./AccountAlert";
import { BlockHeightContext } from "../../../UI/layouts/BaseLayout";
import { AccountVotes } from "./AccountVotes";
import { isAddress } from "../../utils/lisk/utils/lisk/regex";

const ACCOUNT_INFO_QUERY = gql`
  query accountInfo($addressContext: String!) {
    accounts_public(where: { address: { _eq: $addressContext } }) {
      address
      balance
      username
      vote
      secondPublicKey
      rank
      publicKey
      producedBlocks
      missedBlocks
      tag {
        tag
        is_liskhq
        is_exchange
        is_scam
      }
    }
  }
`;
const EXISTS_USERNAME_QUERY = gql`
  query existsUsername($addressContext: String!) {
    usernames_public(where: { username: { _eq: $addressContext } }) {
      username
      address
    }
  }
`;
const LAST_TRANSACTIONS_BY_ACCOUNT_QUERY = gql`
  query transactions($offset: Int!, $limit: Int!, $addressContext: String!) {
    transactions_public(
      limit: $limit
      offset: $offset
      order_by: { timestamp: desc_nulls_last }
      where: {
        _or: [
          { senderId: { _eq: $addressContext } }
          #  {sender: {username: {_eq: $addressContext}}},
          { recipientId: { _eq: $addressContext } }
          #  {recipient: {username: {_eq: $addressContext}}}
        ]
      }
    ) {
      id
      amount
      fee
      recipient {
        address
        username
        affiliation
      }
      recipientId
      senderId
      sender {
        address
        username
        affiliation
      }
      block {
        height
      }
      timestamp
      transferData
      type
      sender_tag {
        tag
      }
      recipient_tag {
        tag
      }
    }
  }
`;
const TOTAL_TX_QUERY = gql`
  query totalTX($addressContext: String!) {
    transactions_public_aggregate(
      where: {
        _or: [
          { senderId: { _eq: $addressContext } }
          { recipientId: { _eq: $addressContext } }
        ]
      }
    ) {
      aggregate {
        count(columns: id)
      }
    }
  }
`;

export const Account: React.FC = () => {
  const limit = 20;
  let { addressContext: addressContextParam, page } = useParams();
  // const [totalTX, setTotalTX] = useState(0);
  const addressContext = isAddress(addressContextParam || "")
    ? addressContextParam
    : addressContextParam?.toLocaleLowerCase();
  const [addressContextReact, setAddressContextReact] = useState(
    addressContext?.toLocaleLowerCase()
  );
  const [pagination, setPagination] = useState(page ? +page : 1);
  const history = useHistory();
  // const [offset, setOffset] = useState(0);
  useEffect(() => {
    setPagination(page ? +page : 1);
    // setTotalTX(0);
    setAddressContextReact(addressContext);
    accountRefetch({
      addressContext: addressContextReact,
    });
    usernameExistsRefetch({
      addressContext,
    });
    totalTXRefetch({
      addressContext: addressContextReact,
    });
  }, [addressContext]);
  const {
    data: usernameExists,
    loading: loadingUsername,
    error: errorUsername,
    refetch: usernameExistsRefetch,
  } = useQuery(EXISTS_USERNAME_QUERY, {
    variables: {
      addressContext,
    },
  });
  const {
    data: transactionsData,
    loading: transactionsLoading,
    error: transactionsError,
    refetch: transactionsRefetch,
  } = useQuery(LAST_TRANSACTIONS_BY_ACCOUNT_QUERY, {
    variables: {
      addressContext: addressContextReact,
      offset: limit * (pagination - 1),
      limit,
    },
    // fetchPolicy: 'no-cache'
  });
  const {
    data: accountData,
    loading: loadingAccountData,
    error: errorAccountData,
    refetch: accountRefetch,
  } = useQuery(ACCOUNT_INFO_QUERY, {
    variables: {
      addressContext: addressContextReact,
    },
    // fetchPolicy: 'no-cache'
  });
  const {
    data: totalTXData,
    loading: loadingTotalTX,
    error: errorTotalTX,
    refetch: totalTXRefetch,
  } = useQuery(TOTAL_TX_QUERY, {
    variables: {
      addressContext: addressContextReact,
    },
    // fetchPolicy: 'no-cache'
  });
  const { data: heightData }: any = React.useContext(BlockHeightContext);

  const handlePagination = (newPage: number) => {
    setPagination(pagination + newPage);
    history.push(`/account/${addressContext}/${pagination + newPage}`);
  };

  if (
    loadingUsername ||
    errorUsername ||
    transactionsLoading ||
    transactionsError ||
    loadingAccountData ||
    errorAccountData ||
    loadingTotalTX ||
    errorTotalTX
  ) {
    return (
      <div className="content">
        <IsErrorOrLoading
          error={
            !!errorUsername ||
            !!errorAccountData ||
            !!transactionsError ||
            !!errorTotalTX
          }
          title={"transactions"}
        />
        ;
      </div>
    );
  }

  if (
    !addressContext ||
    !addressContextReact ||
    (!addressContext.match(/^[1-9]\d{0,19}L$/) &&
      !usernameExists.usernames_public.length)
  ) {
    return (
      <div className="content">
        <Card className="card-chart">
          <CardHeader>
            <CardTitle tag="h4">Transactions</CardTitle>
            <CardBody>
              <CardTitle tag="h4">
                <Badge className={"badge-dark"} style={{ fontSize: "medium" }}>
                  {addressContext}
                </Badge>
                not found in the blockchain :(
              </CardTitle>
            </CardBody>
          </CardHeader>
        </Card>
      </div>
    );
  }
  if (
    !addressContext.match(/^[1-9]\d{0,19}L$/) &&
    !addressContextReact.match(/^[1-9]\d{0,19}L$/)
  ) {
    setAddressContextReact(usernameExists.usernames_public[0].address);
  }

  const transactions = transactionsData.transactions_public;
  const accountInfo = accountData.accounts_public[0];
  const totalTX = totalTXData.transactions_public_aggregate.aggregate.count;
  const blockHeight =
    heightData &&
    heightData.blocks_public &&
    heightData.blocks_public[0] &&
    heightData.blocks_public[0].height
      ? heightData.blocks_public[0].height
      : 0;

  return (
    <>
      <div className="content">
        {!accountInfo || !accountInfo.publicKey ? <AccountAlert /> : null}
        <Row className={"d-block d-sm-none"}>
          <Col md="3">
            {!!accountInfo ? (
              <>
                <AccountDetails
                  address={accountInfo.address}
                  addressTag={accountInfo.tag?.tag || null}
                  isExchange={accountInfo.tag?.is_exchange || null}
                  isScam={accountInfo.tag?.is_scam || null}
                  isLiskhq={accountInfo.tag?.is_liskhq || null}
                  balance={accountInfo.balance}
                  username={accountInfo.username}
                  publicKey={accountInfo.publicKey}
                  secondPublicKey={accountInfo.secondPublicKey}
                  rank={accountInfo.rank}
                />
              </>
            ) : (
              <Card className="card-user">
                <CardBody>
                  <Spinner /> Loading account data
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>
        <Row>
          <Col md="9">
            <Card>
              <CardHeader>
                <h5 className="title">Transactions</h5>
              </CardHeader>
              <CardBody></CardBody>
              <CardFooter>
                <Table responsive>
                  <thead>
                    <tr>
                      <th>TXID</th>
                      <th>Date</th>
                      <th>Sender</th>
                      <th>Recipient</th>
                      <th>Amount</th>
                      <th>Fee</th>
                      <th>Confirm.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx: any) => {
                      return (
                        <TXElement
                          key={tx.id}
                          id={tx.id}
                          timestamp={tx.timestamp}
                          sender={
                            (tx.sender && tx.sender.username) ||
                            (tx.sender_tag && tx.sender_tag.tag) ||
                            tx.senderId
                          }
                          senderId={tx.senderId}
                          recipient={
                            (tx.recipient && tx.recipient.username) ||
                            (tx.recipient_tag && tx.recipient_tag.tag) ||
                            tx.recipientId
                          }
                          recipientId={tx.recipientId}
                          amount={tx.amount}
                          fee={tx.fee}
                          transferData={tx.transferData}
                          confirmations={blockHeight - +tx.block.height}
                          senderAffiliation={
                            (tx.sender && tx.sender.affiliation) || ""
                          }
                          recipientAffiliation={
                            (tx.recipient && tx.recipient.affiliation) || ""
                          }
                          type={tx.type}
                          addressContext={addressContextReact}
                          setAddressContextReact={setAddressContextReact}
                        />
                      );
                    })}
                  </tbody>
                </Table>
                <Row>
                  <Col md="3">
                    {pagination > 1 ? (
                      <Button
                        block
                        color="primary"
                        onClick={() => handlePagination(-1)}
                      >
                        Previous page
                      </Button>
                    ) : null}
                  </Col>
                  <Col md="6" />
                  <Col md="3">
                    {(pagination + 1) * limit < totalTX + limit ? (
                      <Button
                        block
                        color="primary"
                        onClick={() => handlePagination(+1)}
                      >
                        Next page
                      </Button>
                    ) : null}
                  </Col>
                </Row>
              </CardFooter>
            </Card>
          </Col>

          <Col md="3" className={"d-none d-md-block"}>
            {!!accountInfo ? (
              <AccountDetails
                address={accountInfo.address}
                addressTag={accountInfo.tag?.tag || null}
                isExchange={accountInfo.tag?.is_exchange || null}
                isLiskhq={accountInfo.tag?.is_liskhq || null}
                isScam={accountInfo.tag?.is_scam || null}
                balance={accountInfo.balance}
                username={accountInfo.username}
                publicKey={accountInfo.publicKey}
                secondPublicKey={accountInfo.secondPublicKey}
                rank={accountInfo.rank}
              />
            ) : (
              <Card className="card-user">
                <CardBody>
                  <Spinner /> Loading account data
                </CardBody>
              </Card>
            )}
            {!!accountInfo && <AccountVotes address={accountInfo.address} />}
          </Col>
        </Row>
        <Row className={"d-md-none"}>
          <Col xs={12}>
            {!!accountInfo && <AccountVotes address={accountInfo.address} />}
          </Col>
        </Row>
      </div>
    </>
  );
};
